import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, UserCog, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOperatorLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      fetchLogs();
    }
  }, [user]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/operator-logs`);
      setLogs(response.data);
    } catch (error) {
      toast.error('獲取記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => 
    log.operator_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.target_user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
          <div>
            <h1 className="text-3xl font-heading font-bold text-neutral-900">營運者操作記錄</h1>
            <p className="text-neutral-600 mt-1">查看營運者的所有操作（加減點數、審批用戶等）</p>
          </div>
        </header>

        {/* Search */}
        <div className="bg-white border-b border-neutral-200 px-8 py-4 flex-shrink-0">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="搜索營運者、用戶或操作類型..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-lg"
              />
            </div>
            <Button onClick={fetchLogs} variant="outline" className="rounded-lg">
              刷新
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <main className="flex-1 overflow-y-auto p-8">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">時間</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">營運者</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">目標用戶</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">操作</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">點數變動</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">備註</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                        載入中...
                      </td>
                    </tr>
                  ) : filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-neutral-500">
                        暫無記錄
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-neutral-50">
                        <td className="px-6 py-4 text-sm text-neutral-900">
                          {new Date(log.created_at).toLocaleString('zh-TW', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-900">
                          <div className="flex items-center gap-2">
                            <UserCog className="w-4 h-4 text-primary-600" />
                            {log.operator_email}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-900">{log.target_user_email}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            log.action_type === 'approve_user' ? 'bg-green-100 text-green-700' :
                            log.action_type === 'reject_user' ? 'bg-red-100 text-red-700' :
                            log.action_type === 'adjust_credits' ? 'bg-blue-100 text-blue-700' :
                            'bg-neutral-100 text-neutral-700'
                          }`}>
                            {log.action_type === 'approve_user' && '✓ 批准用戶'}
                            {log.action_type === 'reject_user' && '✗ 拒絕用戶'}
                            {log.action_type === 'adjust_credits' && '💰 調整點數'}
                            {log.action_type === 'reset_password' && '🔑 重置密碼'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          {log.credits_change !== undefined && log.credits_change !== 0 && (
                            <span className={log.credits_change > 0 ? 'text-green-600' : 'text-red-600'}>
                              {log.credits_change > 0 ? <Plus className="w-3 h-3 inline mr-1" /> : <Minus className="w-3 h-3 inline mr-1" />}
                              {Math.abs(log.credits_change).toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-neutral-600">{log.notes || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>
    </AdminLayout>
  );
};

export default AdminOperatorLogs;
